import { useTranslation } from "react-i18next";
import { CountryData } from "react-phone-input-2";
import { MAX_INSURED, MAX_PERIOD, MIN_PERIOD } from "src/config";
import i18n from "src/i18n";
import { regexAccents, regexTaxCode, regexVatNumber } from "src/regex";
import {
  ContractorType,
  Nationality,
  PeriodType,
  YesNoAnswersNew,
} from "src/types";
import {
  addStartDays,
  checkFiscalCode,
  checkVatNumberControlChar,
  compareDates,
  formatDate,
  isValidDate,
  replaceDotPhoneFormat,
  reverseDate,
  ystr,
  yupBirthDate,
  yupBool,
  yupBoolRequired,
  yupDateRequired,
  yupEmailRequired,
  yupNum,
  yupStr,
  yupStrRequired,
} from "src/utils";
import * as Yup from "yup";

export const useValidateInsured = () => {
  const { t } = useTranslation();

  return Yup.object().shape({
    insuredName: yupStrRequired.matches(
      regexAccents,
      t("commons.error_is_wrong")
    ),
    insuredSurname: yupStrRequired.matches(
      regexAccents,
      t("commons.error_is_wrong")
    ),
    insuredNationality: yupStr,
    insuredFcQuestion: yupStrRequired,
    insuredBirth: yupBirthDate
      .when("insuredFcQuestion", (item, schema) => {
        return item === YesNoAnswersNew.NO
          ? schema.required(t("commons.required"))
          : schema;
      })
      .test("check_date", t("commons.invalid_date"), (value) => {
        return compareDates(new Date(reverseDate(value)), new Date()) <= 0;
      })
      .test("check_validation", t("commons.invalid_date"), (value, field) => {
        return field.schema.exclusiveTests.required === true
          ? isValidDate(reverseDate(value))
          : true;
      }),
    insuredBirthNation: yupStr
      .when("insuredFcQuestion", (item, schema) => {
        return item === YesNoAnswersNew.NO
          ? schema.required(t("commons.required"))
          : schema;
      })
      .matches(regexAccents, t("commons.error_is_wrong")),
    insuredBirthPlace: yupStr
      .when("insuredFcQuestion", (item, schema) => {
        return item === YesNoAnswersNew.NO
          ? schema.required(t("commons.required"))
          : schema;
      })
      .matches(regexAccents, t("commons.error_is_wrong")),
    insuredGender: yupStr.when("insuredFcQuestion", (item, schema) => {
      return item === YesNoAnswersNew.NO
        ? schema.required(t("commons.required"))
        : schema;
    }),
    insuredFc: yupStr
      .when("insuredFcQuestion", (item, schema) => {
        return item === YesNoAnswersNew.YES
          ? schema.required(t("commons.required"))
          : schema;
      })
      .matches(regexTaxCode, t("commons.error_is_wrong"))
      .test(
        "check_validation",
        t("commons.fiscal_code_mismatch"),
        (value, field) => {
          if (field?.parent?.insuredFcQuestion === YesNoAnswersNew.YES) {
            return (
              checkFiscalCode(
                field?.parent?.insuredName,
                field?.parent?.insuredSurname,
                value || ""
              ) || false
            );
          }
          return true;
        }
      ),
  });
};

const useInputSelectOptionSchema = () => {
  return Yup.object().shape({
    value: yupStrRequired,
  });
};

const useContractorNaturalSchema = () => {
  const { t } = useTranslation();

  const inputSelectOptionSchema = useInputSelectOptionSchema();

  return Yup.object().shape({
    contractorName: yupStrRequired.matches(
      regexAccents,
      t("commons.error_is_wrong")
    ),
    contractorSurname: yupStrRequired.matches(
      regexAccents,
      t("commons.error_is_wrong")
    ),
    contractorResidenceProvince: inputSelectOptionSchema,
    contractorResidenceMunicipality: inputSelectOptionSchema,
    contractorRedisenceAddress: yupStrRequired,
    contractorEmail: yupEmailRequired,
    contractorEmailConfirm: yupEmailRequired.test(
      "check_email",
      t("data_entry_step_2.error_equal_email"),
      (value, field) => {
        return (
          field.parent.contractorEmail?.toLowerCase()?.trim() ===
          value?.toLowerCase()?.trim()
        );
      }
    ),
    contractorPhone: Yup.object().shape({
      formattedPhone: yupStr
        .test(
          "validate-insured-phone",
          t("commons.error_is_wrong"),
          function () {
            const { country, value } = this.parent;

            const formattedValue = value?.replace(/\D/g, "");

            if (
              formattedValue?.length > 0 &&
              formattedValue !== (country as CountryData)?.dialCode &&
              replaceDotPhoneFormat(
                (country as CountryData)?.format,
                formattedValue
              ).includes("X") &&
              formattedValue?.length <
                (country as CountryData)?.dialCode.length + 9
            ) {
              return false;
            }

            return true;
          }
        )
        .required(i18n.t("commons.required")),
    }),
    contractorNationality: Yup.mixed<YesNoAnswersNew>().required(
      t("commons.required")
    ),
    contractorCap: inputSelectOptionSchema,
    contractorFcQuestion: yupStrRequired,
    contractorBirth: yupBirthDate
      .when("contractorFcQuestion", (item, schema) => {
        return item === YesNoAnswersNew.NO
          ? schema.required(t("commons.required"))
          : schema;
      })
      .test("check_date", t("commons.invalid_date"), (value) => {
        return compareDates(new Date(reverseDate(value)), new Date()) <= 0;
      })
      .test("check_validation", t("commons.invalid_date"), (value, field) => {
        return field.schema.exclusiveTests.required === true
          ? isValidDate(reverseDate(value))
          : true;
      }),
    contractorBirthNation: yupStr
      .when("contractorFcQuestion", (item, schema) => {
        return item === YesNoAnswersNew.NO
          ? schema.required(t("commons.required"))
          : schema;
      })
      .matches(regexAccents, t("commons.error_is_wrong")),
    contractorBirthProvince: yupStr
      .when("contractorFcQuestion", (item, schema) => {
        return item === YesNoAnswersNew.NO
          ? schema.required(t("commons.required"))
          : schema;
      })
      .matches(regexAccents, t("commons.error_is_wrong")),
    contractorBirthPlace: yupStr
      .when("contractorFcQuestion", (item, schema) => {
        return item === YesNoAnswersNew.NO
          ? schema.required(t("commons.required"))
          : schema;
      })
      .matches(regexAccents, t("commons.error_is_wrong")),
    contractorGender: yupStr.when("contractorFcQuestion", (item, schema) => {
      return item === YesNoAnswersNew.NO
        ? schema.required(t("commons.required"))
        : schema;
    }),
    contractorFc: yupStr
      .when("contractorFcQuestion", (item, schema) => {
        return item === YesNoAnswersNew.YES
          ? schema
              .required(t("commons.required"))
              .matches(regexTaxCode, t("commons.error_is_wrong"))
          : schema.nullable().notRequired();
      })
      .test(
        "check_validation",
        t("commons.fiscal_code_mismatch"),
        (value, field) => {
          if (field?.parent?.contractorFcQuestion === YesNoAnswersNew.YES) {
            return (
              checkFiscalCode(
                field?.parent?.contractorName,
                field?.parent?.contractorSurname,
                value || ""
              ) || false
            );
          }
          return true;
        }
      ),
  });
};

const useContractorLegalSchema = () => {
  const { t } = useTranslation();

  const inputSelectOptionSchema = useInputSelectOptionSchema();

  return Yup.object().shape({
    companyName: yupStrRequired.matches(
      regexAccents,
      t("commons.error_is_wrong")
    ),
    companyFC: yupStrRequired
      .matches(regexVatNumber, t("commons.error_is_wrong"))
      .test(
        "control_char",
        t("data_entry_step_2.insured_company_fc_controlch_error"),
        (value) => checkVatNumberControlChar(value)
      ),
    companyVAT: yupStr.when("companyFCNotMatchVAT", {
      is: true,
      then: yupStr
        .required(t("commons.required"))
        .matches(regexVatNumber, t("commons.error_is_wrong"))
        .test(
          "fc_not_equal_to_vat",
          t("data_entry_step_2.insured_company_vat_error"),
          (value, ctx) => {
            const fc = ctx.parent.companyFC?.toUpperCase()?.trim();

            if (!fc) return true;

            const vat = value?.toUpperCase()?.trim();
            return fc !== vat;
          }
        )
        .test(
          "control_char",
          t("data_entry_step_2.insured_company_vat_controlch_error"),
          (value) => checkVatNumberControlChar(value)
        ),
      otherwise: yupStr.nullable().notRequired(),
    }),
    companyLegalProvince: inputSelectOptionSchema,
    companyLegalCity: inputSelectOptionSchema,
    companyLegalAddress: yupStrRequired,
    companyLegalCap: inputSelectOptionSchema,
    companyPhone: Yup.object().shape({
      formattedPhone: yupStr
        .test(
          "validate-insured-phone",
          t("commons.error_is_wrong"),
          function () {
            const { country, value } = this.parent;

            const formattedValue = value?.replace(/\D/g, "");

            if (
              formattedValue?.length > 0 &&
              formattedValue !== (country as CountryData)?.dialCode &&
              replaceDotPhoneFormat(
                (country as CountryData)?.format,
                formattedValue
              ).includes("X") &&
              formattedValue?.length <
                (country as CountryData)?.dialCode.length + 9
            ) {
              return false;
            }

            return true;
          }
        )
        .required(i18n.t("commons.required")),
    }),
    companyType: yupStr,
    companyFCNotMatchVAT: yupBool,
    companyEmail: yupEmailRequired,
    companyEmailConfirm: yupEmailRequired.test(
      "check_email",
      t("data_entry_step_2.error_equal_email"),
      (value, field) => {
        return (
          field.parent.companyEmail?.toLowerCase()?.trim() ===
          value?.toLowerCase()?.trim()
        );
      }
    ),
  });
};

export const useValidationSchemas = () => {
  const { t } = useTranslation();

  const otherInsuredValidationSchema = useValidateInsured();

  const maxInsuredNumber = MAX_INSURED;

  const contractorNaturalSchema = useContractorNaturalSchema();

  const contractorLegalSchema = useContractorLegalSchema();
  const step1 = Yup.object().shape({
    insuredNumber: ystr
      .test(
        "check_validation_insuredNumber",
        i18n.t("commons.error_is_wrong"),
        (value) => {
          const numberValue = Number(value);
          return (
            value !== null &&
            value !== undefined &&
            !isNaN(numberValue) &&
            numberValue > 0
          );
        }
      )
      .test(
        "max-insured-number",
        i18n.t("data_entry_step_1.error_max_exceeded_insured_number", {
          number: maxInsuredNumber,
        }),
        (value) => {
          const numberValue = Number(value);
          return numberValue <= maxInsuredNumber;
        }
      ),
    insuredPeriodType: yupStrRequired,
    coverageTypeRadios: yupStrRequired,
    insuredDaysNumber: ystr
      .test(
        "check_validation_insuredDaysNumber",
        i18n.t("commons.error_is_wrong"),
        (value) => {
          const numberValue = Number(value);
          return (
            value !== null &&
            value !== undefined &&
            !isNaN(numberValue) &&
            numberValue >= MIN_PERIOD
          );
        }
      )
      .test(
        "max-insured-days-number",
        i18n.t("data_entry_step_2.error_max_exceeded_insured_days_number", {
          number: MAX_PERIOD,
        }),
        (value) => {
          const numberValue = Number(value);
          return numberValue <= MAX_PERIOD;
        }
      ),
  });

  const step2 = Yup.object().shape({
    coverageStartDate: yupDateRequired?.test(
      "check_date",
      t("commons.invalid_date"),
      (value) => {
        return (
          compareDates(new Date(reverseDate(value)), new Date()) >= 0 &&
          isValidDate(reverseDate(value))
        );
      }
    ),
    coverageEndDate: yupDateRequired
      ?.test("check_date", t("commons.invalid_date"), (value, field) => {
        if (field.parent.insuredPeriodType === PeriodType.DAILY) {
          /* return (
            compareDates(
              new Date(reverseDate(field.parent.coverageStartDate)),
              new Date(reverseDate(value))
            ) === 0 && isValidDate(reverseDate(value))
          );*/
        }
        return !(
          compareDates(
            new Date(reverseDate(field.parent.coverageStartDate)),
            new Date(reverseDate(value))
          ) > 0 && isValidDate(reverseDate(value))
        );
      })
      .test("check_date_2", t("commons.invalid_date"), (value) => {
        return (
          compareDates(new Date(reverseDate(value)), new Date()) >= 0 &&
          isValidDate(reverseDate(value))
        );
      })
      .test(
        "check_date_4",
        t("data_entry_step_2.error_date_period_min") + (MIN_PERIOD + 1),
        (_, field) => {
          if (field.parent.insuredPeriodType === PeriodType.PERIOD) {
            return (
              compareDates(
                new Date(
                  reverseDate(
                    formatDate(
                      addStartDays(
                        reverseDate(field.parent.coverageStartDate),
                        MIN_PERIOD
                      )
                    )
                  )
                ),
                new Date(
                  reverseDate(
                    formatDate(
                      new Date(reverseDate(field.parent.coverageEndDate))
                    )
                  )
                )
              ) <= 0
            );
          }
          return true;
        }
      )
      .test(
        "check_date_5",
        t("data_entry_step_2.error_max_exceeded_insured_days_number", {
          number: MAX_PERIOD,
        }),
        (_, field) => {
          if (field.parent.insuredPeriodType === PeriodType.PERIOD) {
            return (
              compareDates(
                new Date(
                  reverseDate(
                    formatDate(
                      new Date(reverseDate(field.parent.coverageEndDate))
                    )
                  )
                ),
                new Date(
                  reverseDate(
                    formatDate(
                      addStartDays(
                        reverseDate(field.parent.coverageStartDate),
                        MAX_PERIOD - 1
                      )
                    )
                  )
                )
              ) <= 0
            );
          }
          return true;
        }
      ),
    contractorType: yupStrRequired,
    contractor: Yup.lazy((_, ctx) => {
      switch (ctx.parent.contractorType) {
        case ContractorType.LEGAL:
          return contractorLegalSchema;
        case ContractorType.NATURAL:
          return contractorNaturalSchema;
        default:
          return Yup.mixed().notRequired();
      }
    }),
    insuredList: Yup.array().of(otherInsuredValidationSchema),
    consentPrivacy: yupBoolRequired,
    consentMarketing: yupBool,
    consentProfiling: yupBool,
    consentCommunication: yupBool,
  });

  const step3 = Yup.object().shape({
    coherenceQuestions: Yup.array().of(
      Yup.object().shape({
        answer: Yup.mixed<YesNoAnswersNew>().required(t("commons.required")),
      })
    ),
  });

  const step4 = Yup.object().shape({
    informativeVision: yupBoolRequired,
    consentTd: yupBool
      .required(t("commons.consent_required"))
      .oneOf([true], t("commons.consent_required")),
  });

  return [step1, step2, step3, step4];
};

export const useValidationSchemasGroup = () => {
  const { t } = useTranslation();

  const contractorLegalSchema = useContractorLegalSchema();

  const step1 = Yup.object().shape({});

  const step2 = Yup.object().shape({});

  const step3 = Yup.object().shape({
    coverageStartDate: yupDateRequired?.test(
      "check_date",
      t("commons.invalid_date"),
      (value) => {
        return (
          compareDates(new Date(reverseDate(value)), new Date()) >= 0 &&
          isValidDate(reverseDate(value))
        );
      }
    ),
    coverageEndDate: yupDateRequired
      ?.test("check_date", t("commons.invalid_date"), (value, field) => {
        if (field.parent.insuredPeriodType === PeriodType.DAILY) {
          /* return (
            compareDates(
              new Date(reverseDate(field.parent.coverageStartDate)),
              new Date(reverseDate(value))
            ) === 0 && isValidDate(reverseDate(value))
          );*/
        }
        return !(
          compareDates(
            new Date(reverseDate(field.parent.coverageStartDate)),
            new Date(reverseDate(value))
          ) > 0 && isValidDate(reverseDate(value))
        );
      })
      .test("check_date_2", t("commons.invalid_date"), (value) => {
        return (
          compareDates(new Date(reverseDate(value)), new Date()) >= 0 &&
          isValidDate(reverseDate(value))
        );
      })
      .test(
        "check_date_4",
        t("data_entry_step_2.error_date_period_min") + (MIN_PERIOD + 1),
        (_, field) => {
          if (field.parent.insuredPeriodType === PeriodType.PERIOD) {
            return (
              compareDates(
                new Date(
                  reverseDate(
                    formatDate(
                      addStartDays(
                        reverseDate(field.parent.coverageStartDate),
                        MIN_PERIOD
                      )
                    )
                  )
                ),
                new Date(
                  reverseDate(
                    formatDate(
                      new Date(reverseDate(field.parent.coverageEndDate))
                    )
                  )
                )
              ) <= 0
            );
          }
          return true;
        }
      )
      .test(
        "check_date_5",
        t("data_entry_step_2.error_max_exceeded_insured_days_number", {
          number: MAX_PERIOD,
        }),
        (_, field) => {
          if (field.parent.insuredPeriodType === PeriodType.PERIOD) {
            return (
              compareDates(
                new Date(
                  reverseDate(
                    formatDate(
                      new Date(reverseDate(field.parent.coverageEndDate))
                    )
                  )
                ),
                new Date(
                  reverseDate(
                    formatDate(
                      addStartDays(
                        reverseDate(field.parent.coverageStartDate),
                        MAX_PERIOD - 1
                      )
                    )
                  )
                )
              ) <= 0
            );
          }
          return true;
        }
      ),
    contractor: contractorLegalSchema,
    consentPrivacy: yupBoolRequired,
    consentMarketing: yupBool,
    consentProfiling: yupBool,
    consentCommunication: yupBool,
  });
  const step4 = Yup.object().shape({});

  const step5 = Yup.object().shape({
    informativeVision: yupBoolRequired,
    consentTd: yupBool
      .required(t("commons.consent_required"))
      .oneOf([true], t("commons.consent_required")),
  });

  return [step1, step2, step3, step4, step5];
};
